import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { API_URL } from '../lib/apiBase';
import {
  Rss,
  Radio,
  MessageCircle,
  Plus,
  Trash2,
  RefreshCw,
  Sparkles,
  Bell,
  AlertCircle,
} from 'lucide-react';

const KIND_META = {
  reddit:      { icon: MessageCircle, label: 'Reddit',       hint: 'e.g. https://www.reddit.com/r/SaaS/new/.rss' },
  hackernews:  { icon: Radio,         label: 'Hacker News',  hint: 'e.g. https://hnrss.org/newest?q=Show+HN' },
  rss:         { icon: Rss,           label: 'RSS / Blog',   hint: 'Any RSS or Atom feed URL' },
};

const timeAgo = (iso) => {
  if (!iso) return 'never';
  try {
    const now = Date.now();
    const t = new Date(iso).getTime();
    const s = Math.max(1, Math.round((now - t) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
  } catch (_e) {
    return 'recently';
  }
};

const getVisitorId = () => {
  const KEY = 'vibesprint_visitor_id';
  let v = localStorage.getItem(KEY);
  if (!v) {
    v = `vis_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(KEY, v);
  }
  return v;
};

/**
 * SignalSourcesPanel — Phase B main UI
 * Lists a workspace's Signal Sources, adds/removes them, toggles active state,
 * triggers manual fetches, and exposes a subtle waitlist opt-in for the "advanced
 * integrations" that are still on the roadmap (Slack / Intercom / Canny).
 */
export const SignalSourcesPanel = ({ workspaceId, onSignalsIngested }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingId, setFetchingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newKind, setNewKind] = useState('reddit');
  const [newUrl, setNewUrl] = useState('');
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [advancedJoined, setAdvancedJoined] = useState(false);

  const loadSources = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/signal-sources`, {
        params: { workspace_id: workspaceId },
      });
      setSources(data.sources || []);
    } catch (_e) {
      toast.error('Could not load Signal Sources');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    loadSources();
    // Track if this visitor already joined the "advanced integrations" waitlist
    if (localStorage.getItem('vibesprint_wl_signal_sources_advanced')) {
      setAdvancedJoined(true);
    }
  }, [loadSources]);

  const handleToggle = async (source) => {
    const next = !source.is_active;
    setSources((prev) => prev.map((s) => (s.id === source.id ? { ...s, is_active: next } : s)));
    try {
      await axios.patch(`${API_URL}/api/signal-sources/${source.id}`, { is_active: next });
    } catch (_e) {
      toast.error('Failed to update source');
      loadSources();
    }
  };

  const handleDelete = async (source) => {
    if (!window.confirm(`Remove "${source.name}"? Already-ingested signals will stay.`)) return;
    try {
      await axios.delete(`${API_URL}/api/signal-sources/${source.id}`);
      setSources((prev) => prev.filter((s) => s.id !== source.id));
      toast.success('Source removed');
    } catch (_e) {
      toast.error('Failed to remove source');
    }
  };

  const handleFetchOne = async (source) => {
    setFetchingId(source.id);
    try {
      const { data } = await axios.post(`${API_URL}/api/signal-sources/${source.id}/fetch`);
      const added = data.new_signals || 0;
      if (added > 0) {
        toast.success(`Fetched ${added} new signal${added === 1 ? '' : 's'} from ${source.name}`);
        if (onSignalsIngested) onSignalsIngested();
      } else {
        toast.info(`No new signals from ${source.name}`, {
          description: 'The feed had no new items since the last poll.',
        });
      }
      loadSources();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Fetch failed';
      toast.error(detail);
    } finally {
      setFetchingId(null);
    }
  };

  const handleFetchAll = async () => {
    const active = sources.filter((s) => s.is_active);
    if (active.length === 0) {
      toast.info('No active sources to fetch');
      return;
    }
    let total = 0;
    for (const src of active) {
      try {
        // Fetch each source sequentially to be polite to feeds
        const { data } = await axios.post(`${API_URL}/api/signal-sources/${src.id}/fetch`);
        total += data.new_signals || 0;
      } catch (_e) {
        // ignore per-source error; will be reflected in last_error
      }
    }
    toast.success(`Fetched ${total} new signal${total === 1 ? '' : 's'} across ${active.length} source${active.length === 1 ? '' : 's'}`);
    if (onSignalsIngested && total > 0) onSignalsIngested();
    loadSources();
  };

  const handleCreate = async () => {
    if (!newUrl.trim()) {
      toast.error('URL is required');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/signal-sources`, {
        workspace_id: workspaceId,
        kind: newKind,
        url: newUrl.trim(),
        name: newName.trim() || undefined,
      });
      toast.success('Signal source connected');
      setDialogOpen(false);
      setNewUrl('');
      setNewName('');
      setNewKind('reddit');
      loadSources();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not add source';
      toast.error(detail);
    } finally {
      setCreating(false);
    }
  };

  const handleJoinAdvancedWaitlist = async () => {
    try {
      await axios.post(`${API_URL}/api/waitlist`, {
        feature: 'signal_sources_advanced',
        visitor_id: getVisitorId(),
        source: 'signal_sources_panel',
      });
      localStorage.setItem('vibesprint_wl_signal_sources_advanced', 'true');
      setAdvancedJoined(true);
      toast.success("You're on the list", {
        description: "We'll ping you when Slack, Intercom & Canny integrations ship.",
      });
    } catch (_e) {
      toast.error('Could not join the waitlist right now');
    }
  };

  return (
    <Card
      className="p-4 sm:p-5 border-primary/20"
      data-testid="signal-sources-panel"
    >
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="rounded-md bg-primary/10 p-1.5">
            <Rss className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold tracking-tight" data-testid="signal-sources-panel-title">
                Signal Sources
              </h3>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary/40 text-primary">Beta</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Auto-fetch feedback nightly. Manage active sources or trigger a fetch now.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleFetchAll}
            disabled={loading || sources.length === 0}
            data-testid="signal-sources-fetch-all"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Fetch all
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="btn-cta" data-testid="signal-sources-add-open">
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add source
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="signal-sources-add-dialog">
              <DialogHeader>
                <DialogTitle>Connect a Signal Source</DialogTitle>
                <DialogDescription>
                  Paste any Reddit, Hacker News, or RSS/Atom feed. VibeSprint will fetch it nightly and drop new items into your lanes.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label htmlFor="src-kind">Type</Label>
                  <Select value={newKind} onValueChange={setNewKind}>
                    <SelectTrigger id="src-kind" data-testid="signal-sources-kind-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(KIND_META).map(([k, meta]) => (
                        <SelectItem key={k} value={k}>
                          <div className="flex items-center gap-2">
                            <meta.icon className="h-3.5 w-3.5" />
                            {meta.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="src-url">Feed URL</Label>
                  <Input
                    id="src-url"
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    placeholder={KIND_META[newKind].hint}
                    data-testid="signal-sources-url-input"
                  />
                  <p className="text-xs text-muted-foreground">{KIND_META[newKind].hint}</p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="src-name">Display name <span className="text-muted-foreground">(optional)</span></Label>
                  <Input
                    id="src-name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="e.g. r/SaaS · new"
                    data-testid="signal-sources-name-input"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="ghost" onClick={() => setDialogOpen(false)} data-testid="signal-sources-add-cancel">Cancel</Button>
                <Button
                  onClick={handleCreate}
                  disabled={creating}
                  className="btn-cta"
                  data-testid="signal-sources-add-submit"
                >
                  {creating ? 'Connecting…' : 'Connect source'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Sources list */}
      {sources.length === 0 ? (
        <div
          className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground"
          data-testid="signal-sources-empty"
        >
          <Sparkles className="h-4 w-4 mx-auto mb-2 text-primary" />
          No sources connected yet. Add a Reddit / HN / RSS feed to start receiving signals.
        </div>
      ) : (
        <ul className="divide-y rounded-lg border" data-testid="signal-sources-list">
          {sources.map((src) => {
            const Meta = KIND_META[src.kind] || KIND_META.rss;
            const Icon = Meta.icon;
            const isFetching = fetchingId === src.id;
            return (
              <li
                key={src.id}
                className="flex items-center gap-3 p-3"
                data-testid={`signal-source-row-${src.id}`}
              >
                <div className="rounded-md bg-muted p-2 flex-shrink-0">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate">{src.name}</span>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">{Meta.label}</Badge>
                    {typeof src.signals_this_month === 'number' && (
                      <Badge
                        variant="secondary"
                        className={`text-[10px] px-1.5 py-0 tabular-nums ${
                          src.signals_this_month > 0
                            ? 'bg-primary/10 text-primary hover:bg-primary/15'
                            : ''
                        }`}
                        title={`${src.signals_this_month} signal${src.signals_this_month === 1 ? '' : 's'} added this month`}
                        data-testid={`signal-source-count-${src.id}`}
                      >
                        {src.signals_this_month} this mo
                      </Badge>
                    )}
                    {!src.is_active && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Paused</Badge>
                    )}
                    {src.last_error && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] text-destructive"
                        title={src.last_error}
                        data-testid={`signal-source-error-${src.id}`}
                      >
                        <AlertCircle className="h-3 w-3" />
                        error
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {src.url}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Last synced: {timeAgo(src.last_fetched_at)}
                  </div>
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Switch
                    checked={!!src.is_active}
                    onCheckedChange={() => handleToggle(src)}
                    aria-label={src.is_active ? 'Pause source' : 'Resume source'}
                    data-testid={`signal-source-toggle-${src.id}`}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleFetchOne(src)}
                    disabled={isFetching || !src.is_active}
                    aria-label="Fetch now"
                    data-testid={`signal-source-fetch-${src.id}`}
                    title="Fetch now"
                  >
                    <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleDelete(src)}
                    aria-label="Remove source"
                    data-testid={`signal-source-delete-${src.id}`}
                    title="Remove source"
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Advanced integrations waitlist strip */}
      <div
        className="mt-4 rounded-md border border-dashed p-3 flex items-center justify-between gap-3 flex-wrap"
        data-testid="signal-sources-advanced-waitlist"
      >
        <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
          <Bell className="h-3.5 w-3.5 text-primary flex-shrink-0" />
          <span className="min-w-0">
            <span className="font-medium text-foreground">Slack · Intercom · Canny</span> integrations coming next.
          </span>
        </div>
        {advancedJoined ? (
          <Badge variant="secondary" className="text-[10px]">You&apos;re on the list</Badge>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={handleJoinAdvancedWaitlist}
            data-testid="signal-sources-advanced-notify"
          >
            Notify me
          </Button>
        )}
      </div>
    </Card>
  );
};

export default SignalSourcesPanel;
